import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';

import { BaseForm } from './BaseForm';
import { FormField } from './FormField';
import { userSchema, UserFormData } from '@/utils/validation.ts';

interface RegistrationFormProps {
  onSubmit: (data: UserFormData) => void | Promise<void>;
  loading?: boolean;
  error?: string;
  success?: string;
}

export const RegistrationForm = ({
  onSubmit,
  loading = false,
  error,
  success,
}: RegistrationFormProps) => {
  const [showPassword, setShowPassword] = useState(false);

  const defaultValues: Partial<UserFormData> = {
    name: '',
    email: '',
    phone: '',
    age: undefined,
    password: '',
    confirmPassword: '',
  };

  return (
    <BaseForm
      title="Регистрация"
      schema={userSchema}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      loading={loading}
    >
      {(form: UseFormReturn<UserFormData>) => (
        <>
          {error && (
            <Alert variant="filled" severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="filled" severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <FormField
            name="name"
            control={form.control}
            label="Полное имя"
            placeholder="Введите ваше полное имя"
          />

          <FormField
            name="email"
            control={form.control}
            label="Email"
            type="email"
            placeholder="example@email.com"
          />

          <FormField
            name="phone"
            control={form.control}
            label="Телефон"
            type="tel"
            placeholder="+7 (999) 123-45-67"
          />

          <FormField
            name="age"
            control={form.control}
            label="Возраст"
            type="number"
            placeholder="25"
          />

          <FormField
            name="password"
            control={form.control}
            label="Пароль"
            type={showPassword ? 'text' : 'password'}
            placeholder="Введите пароль"
          />

          <FormField
            name="confirmPassword"
            control={form.control}
            label="Подтвердите пароль"
            type={showPassword ? 'text' : 'password'}
            placeholder="Подтвердите пароль"
          />

          <Box display="flex" alignItems="center" gap={1}>
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? 'Скрыть' : 'Показать'} пароль
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box display="flex" gap={2} justifyContent="center">
            <Button
              type="button"
              variant="outlined"
              onClick={() => form.reset()}
              disabled={loading}
            >
              Очистить
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={
                loading ? <CircularProgress size={20} /> : <PersonAddIcon />
              }
              disabled={loading || !form.formState.isValid}
            >
              {loading ? 'Регистрация...' : 'Зарегистрироваться'}
            </Button>
          </Box>

          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mt: 2 }}
          >
            Нажимая "Зарегистрироваться", вы соглашаетесь с условиями
            использования
          </Typography>
        </>
      )}
    </BaseForm>
  );
};
